import type { AgentBox, SpawnRequest, AcceptedResponse, OperationResult } from '../types';
import { getAccessToken } from '../auth/tokenHelper';

const API_BASE = import.meta.env.VITE_API_URL || '';

async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options?.headers,
    },
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

export interface SessionInfo {
  name: string;
  windows: number;
  createdAt: number;
  attached: boolean;
  copilotRunning: boolean;
  ttydPort: number | null;
}

export const api = {
  listInstances: () => fetchApi<AgentBox[]>('/api/instances'),
  getInstance: (id: string) => fetchApi<AgentBox>(`/api/instances/${id}`),
  spawnInstance: (req: SpawnRequest) =>
    fetchApi<AcceptedResponse>('/api/instances', { method: 'POST', body: JSON.stringify(req) }),
  destroyInstance: (id: string) =>
    fetchApi<AcceptedResponse>(`/api/instances/${id}`, { method: 'DELETE' }),
  getOperation: (id: string) =>
    fetchApi<OperationResult>(`/api/operations/${id}`),
  getInstanceStatus: (id: string) =>
    fetchApi<{ status: string }>(`/api/instances/${id}/status`),
  getProxyToken: (instanceId: string) =>
    fetchApi<{ token: string }>('/api/proxy/token', {
      method: 'POST',
      body: JSON.stringify({ instanceId }),
    }),
  listSessions: (instanceId: string) =>
    fetchApi<SessionInfo[]>(`/api/instances/${instanceId}/sessions`),
  createSession: (instanceId: string, name: string, command?: string) =>
    fetchApi<{ name: string; port: number; status: string }>(`/api/instances/${instanceId}/sessions`, {
      method: 'POST',
      body: JSON.stringify({ name, ...(command !== undefined && { command }) }),
    }),
  deleteSession: (instanceId: string, name: string) =>
    fetchApi<{ deleted: string }>(`/api/instances/${instanceId}/sessions/${name}`, {
      method: 'DELETE',
    }),
  /** Sign an SSH public key (OpenSSH wire format, base64) and get back a certificate */
  signSshKey: (instanceId: string, publicKeyBase64: string) =>
    fetchApi<{ certificate: string }>(`/api/instances/${instanceId}/ssh-cert`, {
      method: 'POST',
      body: JSON.stringify({ publicKey: publicKeyBase64 }),
    }),
  /** GitHub OAuth authorization URL — open in a popup */
  getGitHubOAuthUrl: () => `${API_BASE}/auth/github`,
  /** Azure DevOps OAuth authorization URL — open in a popup */
  getAdoOAuthUrl: () => `${API_BASE}/auth/ado`,
  /** Atlassian (Jira) OAuth authorization URL — open in a popup */
  getAtlassianOAuthUrl: () => `${API_BASE}/auth/atlassian`,
};
